import { disabledTokens } from '@federa/utils/styles/interaction-tokens';
import { FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { colors } from '../css-refs.colors.ts';
import { typography, fontFamilyCSS } from '../css-refs.typography.ts';
import { spacing } from '../css-refs.spacing.ts';
import { borderRadius } from '../css-refs.effects.ts';
import { disabledOpacity, TRANSITION_FAST } from '../common/layout-styles.ts';
import { divider } from '../css-refs.layout.ts';
import { adornmentBaseStyles } from './input-helpers.ts';
import type * as React from 'react';
import type { ComponentSizeFacets } from '@federa/types/ui-design-tokens';
import type { InputStateStyle } from '@federa/types/ui-input';

/** Base addon styles - extends shared adornment base with Input-specific extras */
const addonBaseStyles: React.CSSProperties = {
	...adornmentBaseStyles,
	justifyContent: 'center',
	color: colors.text.tertiary,
};

/** Where an addon sits in from the box's edge -- `leftAddonStyles` / `rightAddonStyles` below, and the inset the
 *  input's own padding is measured from on that side. */
const ADDON_INSET = spacing[3];

/**
 * The input's padding on a side an addon occupies: the addon's inset, plus the addon's own width, plus the box's
 * ordinary horizontal padding -- so the text stands one padding step from the addon, as it stands from the edge.
 *
 * A TEXT addon is measured by its characters (`ch`, the font's digit width, which the sign and the unit sit inside):
 * on the one fixed reserve (`spacing[10]`) a "$" stood a 20px void from its digits where the same figure elsewhere set
 * the sign against them (critics, 2026-09-18). A NODE addon (an icon) keeps the fixed reserve, since its width is not
 * legible here.
 */
function addonSidePadding(addon: React.ReactNode, horizontalPadding: string): string {
	if (typeof addon !== 'string') return spacing[10];
	return `calc(${ADDON_INSET} + ${String(addon.length)}ch + ${horizontalPadding})`;
}

export function buildInputStyles(options: {
	sizeStyle: ComponentSizeFacets;
	stateStyle: InputStateStyle;
	isFocused: boolean;
	disabled: boolean | undefined;
	leftAddon: React.ReactNode | undefined;
	rightAddon: React.ReactNode | undefined;
}): React.CSSProperties {
	const { sizeStyle, stateStyle, isFocused, disabled, leftAddon, rightAddon } = options;
	const paddingParts = sizeStyle.padding.split(' ');
	const horizontalPadding = paddingParts[1] ?? paddingParts[0] ?? '';

	return {
		flex: 1,
		fontFamily: fontFamilyCSS.sans,
		fontSize: sizeStyle.fontSize,
		height: sizeStyle.height,
		padding: sizeStyle.padding,
		paddingLeft: leftAddon ? addonSidePadding(leftAddon, horizontalPadding) : horizontalPadding,
		paddingRight: rightAddon ? addonSidePadding(rightAddon, horizontalPadding) : horizontalPadding,
		backgroundColor: disabled ? colors.background.tertiary : colors.background.primary,
		border: `${divider.thickness.thin} solid ${isFocused ? stateStyle.focusBorderColor : stateStyle.borderColor}`,
		borderRadius: borderRadius.md,
		color: disabled ? colors.text.disabled : colors.text.primary,
		// THE NATIVE ACCENT IS THE BRAND'S (iss-8eb9d957): a range or progress control's track and thumb are the user
		// agent's to draw, in its own blue unless the control names an accent -- the Sankey's band slider did.
		accentColor: colors.brand.primary,
		outline: 'none',
		transition: TRANSITION_FAST,
		cursor: disabled ? disabledTokens.cursor : 'text',
		opacity: disabledOpacity(disabled),
		...FULL_WIDTH_STYLE,
	};
}

export const leftAddonStyles: React.CSSProperties = {
	...addonBaseStyles,
	left: ADDON_INSET,
};
export const rightAddonStyles: React.CSSProperties = {
	...addonBaseStyles,
	right: ADDON_INSET,
};

export function buildFloatingContainerStyles(
	stateStyle: InputStateStyle,
	isFocused: boolean,
	disabled: boolean | undefined,
): React.CSSProperties {
	return {
		position: 'relative',
		backgroundColor: disabled ? colors.background.tertiary : colors.background.primary,
		border: `${divider.thickness.thin} solid ${isFocused ? stateStyle.focusBorderColor : stateStyle.borderColor}`,
		borderRadius: borderRadius.full,
		transition: TRANSITION_FAST,
		opacity: disabledOpacity(disabled),
	};
}

export function buildFloatingInputStyles(disabled: boolean | undefined): React.CSSProperties {
	return {
		...FULL_WIDTH_STYLE,
		fontFamily: fontFamilyCSS.sans,
		fontSize: typography.fontSize.base,
		height: spacing[14],
		padding: `${spacing[5]} ${spacing[4]} ${spacing[2]}`,
		backgroundColor: 'transparent',
		border: 'none',
		borderRadius: borderRadius.full,
		color: disabled ? colors.text.disabled : colors.text.primary,
		outline: 'none',
		cursor: disabled ? disabledTokens.cursor : 'text',
	};
}

export function buildFloatingLabelStyles(isFloating: boolean, isFocused: boolean): React.CSSProperties {
	const floatingTop = spacing[1.5];
	const restingTop = '50%';
	const floatingFontSize = typography.fontSize.xs;
	const restingFontSize = typography.fontSize.base;

	return {
		position: 'absolute',
		left: spacing[4],
		top: isFloating ? floatingTop : restingTop,
		transform: isFloating ? 'translateY(0)' : 'translateY(-50%)',
		fontSize: isFloating ? floatingFontSize : restingFontSize,
		fontWeight: isFloating ? typography.fontWeight.medium : typography.fontWeight.normal,
		color: isFocused ? colors.brand.primary : colors.text.tertiary,
		backgroundColor: isFloating ? colors.background.primary : 'transparent',
		padding: isFloating ? `0 ${spacing[1]}` : '0',
		borderRadius: borderRadius.xs,
		pointerEvents: 'none',
		transition: TRANSITION_FAST,
		lineHeight: typography.lineHeight.none,
	};
}
